import { useState, useEffect, useMemo, useLayoutEffect, useCallback, memo } from 'react';
import { Link } from 'react-router-dom';
import { useCruises } from '../context/CruiseContext';
import { useCruiseMetadata } from '../context/CruiseMetadataContext';
import { useItinerary } from '../context/ItineraryContext';
import { Ship, ChevronDown, ChevronUp, Anchor, Trophy, CheckCircle, Calendar, Wrench, XCircle, ExternalLink } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { CollectionSkeleton } from '../components/LoadingSkeleton';
import LazyImage from '../components/LazyImage';
import CustomCruiseIcon from '../components/CustomCruiseIcon';
import { parseISO, isAfter, startOfDay, startOfYear } from 'date-fns';

interface CruiseLineCollection {
  cruiseLineName: string;
  totalActiveShips: number;
  sailedShips: string[];
  notSailedShips: string[];
  bookedShips: string[];
  underConstructionShips: string[];
  sailedCount: number;
  percentage: number;
  retiredShipsSailed: string[];
  allBookedShips: string[];
}

const SESSION_ITINERARY_CACHE_KEY = 'collectionItineraryCache';

// Module-level cache so itinerary summaries persist across page navigations in the current session
const globalItineraryCache = new Map<string, { departureDates: string[] }>();
let globalItineraryLoaded = false;

const getInitialItineraryCache = (): Map<string, { departureDates: string[] }> => {
  if (globalItineraryCache.size > 0) {
    return new Map(globalItineraryCache);
  }
  try {
    const saved = sessionStorage.getItem(SESSION_ITINERARY_CACHE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      const map = new Map<string, { departureDates: string[] }>(Object.entries(parsed));
      map.forEach((v, k) => globalItineraryCache.set(k, v));
      globalItineraryLoaded = true;
      return map;
    }
  } catch (e) {
    console.error('Error reading itinerary cache from sessionStorage:', e);
  }
  return new Map();
};

interface ShipCardProps {
  shipName: string;
  sailed: boolean;
  isBooked?: boolean;
  underConstruction?: boolean;
  cruiseLineName: string;
  shipImageUrl: string | null;
  onShipClick: (cruiseLineName: string, shipName: string) => void;
}

const ShipCard = memo(({
  shipName,
  sailed,
  isBooked,
  underConstruction,
  cruiseLineName,
  shipImageUrl,
  onShipClick,
}: ShipCardProps) => {
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    setImageError(false);
  }, [shipImageUrl]);

  const shipDetailsUrl = `/ship/${encodeURIComponent(cruiseLineName)}/${encodeURIComponent(shipName)}`;
  const cardId = `ship-card-${cruiseLineName}___${shipName}`.toLowerCase().replace(/[^a-z0-9]/g, '-');

  return (
    <Link to={shipDetailsUrl} onClick={() => onShipClick(cruiseLineName, shipName)} id={cardId}>
      <motion.div
        whileHover={{ scale: 1.02 }}
        transition={{ duration: 0.2 }}
        className={`bg-white rounded-lg shadow-md overflow-hidden cursor-pointer transition-shadow hover:shadow-lg ${
          sailed ? 'border-2 border-secondary-500' : 'border border-gray-200 opacity-60'
        }`}
      >
        <div className="h-32 relative overflow-hidden bg-gradient-to-r from-blue-900 to-blue-700">
          {/* Booked Banner */}
          {isBooked && (
            <div className="absolute top-0 left-0 right-0 z-10 bg-gradient-to-r from-blue-500 to-blue-600 text-white px-2 py-1 flex items-center justify-center">
              <Calendar className="h-3 w-3 mr-1" />
              <span className="text-xs font-medium">Booked</span>
            </div>
          )}
          
          {shipImageUrl && !imageError ? (
            <>
              <LazyImage 
                src={shipImageUrl}
                alt={shipName}
                className="w-full h-full object-cover"
                onError={() => setImageError(true)}
              />
              <div className="absolute bottom-0 left-0 right-0 h-1/2 bg-gradient-to-t from-black/40 to-transparent"></div>
            </>
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <CustomCruiseIcon size={64} className="text-white/30" />
            </div>
          )}
          {sailed && (
            <div className="absolute top-2 right-2 z-10">
              <div className="bg-secondary-600 text-white rounded-full p-1">
                <CheckCircle className="h-4 w-4" />
              </div>
            </div>
          )}
          {!sailed && underConstruction && (
            <div className={`absolute right-2 z-10 ${isBooked ? 'top-8' : 'top-2'}`}>
              <div className="bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-full p-1">
                <Wrench className="h-4 w-4" />
              </div>
            </div>
          )}
          {!sailed && !underConstruction && !isBooked && (
            <div className="absolute top-2 right-2 z-10">
              <div className="bg-gray-600 text-white rounded-full p-1">
                <XCircle className="h-4 w-4" />
              </div>
            </div>
          )}
        </div>
        <div className="p-4">
          <h4 className={`font-semibold text-sm ${sailed ? 'text-gray-900' : 'text-gray-500'}`}>
            {shipName}
          </h4>
          <div className="flex items-center justify-end mt-3 pt-3 border-t border-gray-200">
            <div className="flex items-center text-sm font-medium text-secondary-600 hover:text-secondary-500 transition-colors duration-200">
              <ExternalLink className="h-4 w-4 mr-1" />
              View Details
            </div>
          </div>
        </div>
      </motion.div>
    </Link>
  );
});
ShipCard.displayName = 'ShipCard';

const Collection = () => {
  const { cruises, loading } = useCruises();
  const { cruiseLines, getShipsForCruiseLine, getShipImageUrl, getShipMetadata, loading: metadataLoading } = useCruiseMetadata();
  const { getItinerarySummaryForShip } = useItinerary();

  // State for scroll position memory
  const [rememberedScrollY, setRememberedScrollY] = useState<number>(() => {
    try {
      const stored = sessionStorage.getItem('collectionScrollY');
      if (stored) {
        const parsed = parseInt(stored, 10);
        return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
      }
    } catch (e) {
      console.error('Error reading collectionScrollY:', e);
    }
    return 0;
  });

  const isRestoringScroll = rememberedScrollY > 0;

  // State for expanded cruise line accordions, restored from sessionStorage
  const [expandedLines, setExpandedLines] = useState<Set<string>>(() => {
    const lines = new Set<string>();
    try {
      const saved = sessionStorage.getItem('collectionExpandedLines');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          parsed.forEach((item) => lines.add(item));
        }
      }
      const targetShip = sessionStorage.getItem('collectionTargetShip');
      if (targetShip) {
        const [targetCruiseLine] = targetShip.split('___');
        if (targetCruiseLine) {
          lines.add(targetCruiseLine);
        }
      }
    } catch (e) {
      console.error('Error restoring expanded cruise lines:', e);
    }
    return lines;
  });

  const [dataLoaded, setDataLoaded] = useState(false);
  const [minTimeElapsed, setMinTimeElapsed] = useState(false);
  const [itineraryCache, setItineraryCache] = useState<Map<string, { departureDates: string[] }>>(
    () => getInitialItineraryCache()
  );
  const [itineraryLoaded, setItineraryLoaded] = useState(
    () => globalItineraryLoaded || globalItineraryCache.size > 0
  );

  // Ensure skeleton shows for minimum duration (500ms, matching Dashboard)
  useEffect(() => {
    const timer = setTimeout(() => {
      setMinTimeElapsed(true);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  // Persist expandedLines to sessionStorage whenever it changes
  useEffect(() => {
    try {
      sessionStorage.setItem('collectionExpandedLines', JSON.stringify(Array.from(expandedLines)));
    } catch (e) {
      console.error('Error saving expanded cruise lines:', e);
    }
  }, [expandedLines]);

  // Track when data is actually loaded (cruises, metadata, AND itinerary summaries)
  useEffect(() => {
    if (!loading && !metadataLoading && itineraryLoaded) {
      setDataLoaded(true);
    } else {
      setDataLoaded(false);
    }
  }, [loading, metadataLoading, itineraryLoaded]);

  // Restore scroll position before paint to avoid flash of top of page (useLayoutEffect runs after DOM update, before browser paint)
  useLayoutEffect(() => {
    if (dataLoaded && minTimeElapsed && rememberedScrollY > 0) {
      window.scrollTo(0, rememberedScrollY);
      sessionStorage.removeItem('collectionScrollY');

      const targetShip = sessionStorage.getItem('collectionTargetShip');
      sessionStorage.removeItem('collectionTargetShip');

      // If target ship card is available, verify it is in view after DOM renders
      if (targetShip) {
        const elementId = `ship-card-${targetShip}`.toLowerCase().replace(/[^a-z0-9]/g, '-');
        requestAnimationFrame(() => {
          const el = document.getElementById(elementId);
          if (el) {
            const rect = el.getBoundingClientRect();
            if (rect.bottom < 0 || rect.top > window.innerHeight) {
              el.scrollIntoView({ block: 'center', behavior: 'auto' });
            }
          }
        });
      }

      setRememberedScrollY(0); // Reset to prevent re-scrolling
    }
  }, [dataLoaded, minTimeElapsed, rememberedScrollY]);

  // Load itinerary data for ships that might be under construction
  useEffect(() => {
    const loadItineraryData = async () => {
      if (!cruiseLines || cruiseLines.length === 0 || !cruises || cruises.length === 0) return;
      
      // If already loaded into cache, no need to re-fetch on every sub-navigation
      if (globalItineraryLoaded && globalItineraryCache.size > 0) {
        setItineraryLoaded(true);
        return;
      }

      const cache = new Map<string, { departureDates: string[] }>(globalItineraryCache);
      const currentYear = new Date().getFullYear();
      const now = startOfDay(new Date());
      
      // Get all sailed ships
      const sailedShipsSet = new Set(
        cruises
          .filter(c => startOfDay(parseISO(c.departureDate)) <= now)
          .map(c => `${c.cruiseLine}_${c.shipName}`.toLowerCase())
      );
      
      const promises: Promise<void>[] = [];

      for (const cruiseLine of cruiseLines) {
        const shipsData = getShipsForCruiseLine(cruiseLine.name);
        const activeShips = shipsData.active || [];
        
        for (const shipName of activeShips) {
          const shipKey = `${cruiseLine.name}_${shipName}`.toLowerCase();
          
          // Skip if ship has been sailed
          if (sailedShipsSet.has(shipKey)) {
            continue;
          }

          // Skip if already in cache
          if (cache.has(shipKey)) {
            continue;
          }
          
          const shipMetadata = getShipMetadata(cruiseLine.name, shipName);
          const yearBuilt = shipMetadata?.yearBuilt ? parseInt(shipMetadata.yearBuilt, 10) : null;
          
          // Check itinerary for ships that might be under construction:
          // 1. Ships with future yearBuilt
          // 2. Ships that haven't been sailed yet (to see if they have itinerary data)
          const shouldCheckItinerary = 
            (yearBuilt && yearBuilt >= currentYear) || // Future or current year
            !yearBuilt; // No yearBuilt info, check itinerary to see if ship exists
          
          if (shouldCheckItinerary) {
            promises.push((async () => {
              try {
                const summary = await getItinerarySummaryForShip(cruiseLine.name, shipName);
                if (summary && summary.departureDates && summary.departureDates.length > 0) {
                  cache.set(shipKey, { departureDates: summary.departureDates });
                }
              } catch {
                // Silently fail - ship might not have itinerary data
              }
            })());
          }
        }
      }
      
      // Execute all network requests simultaneously
      await Promise.all(promises);

      // Save to global cache & sessionStorage
      for (const [k, v] of cache.entries()) {
        globalItineraryCache.set(k, v);
      }
      globalItineraryLoaded = true;
      try {
        const obj = Object.fromEntries(globalItineraryCache);
        sessionStorage.setItem(SESSION_ITINERARY_CACHE_KEY, JSON.stringify(obj));
      } catch {
        // Ignore quota
      }

      setItineraryCache(new Map(globalItineraryCache));
      setItineraryLoaded(true);
    };
    
    if (!metadataLoading && cruiseLines.length > 0 && cruises.length > 0) {
      loadItineraryData();
    }
  }, [cruiseLines, cruises, metadataLoading, getShipsForCruiseLine, getShipMetadata, getItinerarySummaryForShip]);

  // Helper function to check if a ship is under construction
  const isShipUnderConstruction = (
    cruiseLineName: string,
    shipName: string,
    sailedShipsSet: Set<string>
  ): boolean => {
    const shipMetadata = getShipMetadata(cruiseLineName, shipName);
    const yearBuilt = shipMetadata?.yearBuilt ? parseInt(shipMetadata.yearBuilt, 10) : null;
    const currentYear = new Date().getFullYear();
    const now = startOfDay(new Date());
    
    // Check if ship has been sailed - if so, it's not under construction
    const hasBeenSailed = sailedShipsSet.has(shipName.toLowerCase());
    if (hasBeenSailed) {
      return false;
    }
    
    // Check itinerary data (key lowercased to match loader; avoids mismatch if names differ by case)
    const itineraryKey = `${cruiseLineName}_${shipName}`.toLowerCase();
    const itineraryData = itineraryCache.get(itineraryKey);
    const hasItineraryData = itineraryData && itineraryData.departureDates.length > 0;
    
    // If yearBuilt is in the future, ship is under construction
    if (yearBuilt && yearBuilt > currentYear) {
      return true;
    }
    
    // If ship has itinerary data but hasn't been sailed yet
    if (hasItineraryData) {
      // Sort dates and get the first one
      const sortedDates = itineraryData.departureDates.sort();
      
      // Extract the real date if it's a composite key (e.g., '2026-07-04_2026-07-11')
      const firstValidDateString = sortedDates[0].includes('_') ? sortedDates[0].split('_')[0] : sortedDates[0];
      const firstSailingDate = startOfDay(parseISO(firstValidDateString));
      
      // If first sailing is today or in the future, ship is still under construction
      // If first sailing is in the past, ship moves to "not sailed"
      return firstSailingDate >= now;
    }
    
    // If no itinerary data, check based on yearBuilt
    if (yearBuilt) {
      const yearStart = startOfYear(new Date(yearBuilt, 0, 1));
      
      // If current date is before Jan 1 of the yearBuilt year, ship is under construction
      if (now < yearStart) {
        return true;
      }
      
      // If yearBuilt is current year or future but we don't have itinerary data yet (e.g. still loading),
      // treat as under construction to avoid bouncing to "ships not sailed" until we know first sailing date.
      if (yearBuilt >= currentYear) {
        return true;
      }
      
      // yearBuilt is in the past and we're on or after Jan 1 of that year → "not sailed"
      return false;
    }
    
    // If no yearBuilt and no itinerary data, ship is not under construction
    return false;
  };

  // Calculate collection data for each cruise line
  const collectionData = useMemo(() => {
    if (!cruises || cruises.length === 0 || !cruiseLines || cruiseLines.length === 0) {
      return [];
    }

    // Get unique cruise lines from user's cruises
    const userCruiseLines = new Set(cruises.map(c => c.cruiseLine));
    
    const collections: CruiseLineCollection[] = [];

    userCruiseLines.forEach(cruiseLineName => {
      // Get ships for this cruise line from metadata
      const shipsData = getShipsForCruiseLine(cruiseLineName);
      const activeShips = shipsData.active || [];
      const retiredShips = shipsData.retired || [];
      
      if (activeShips.length === 0) {
        return; // Skip if no active ships in metadata
      }

      const now = startOfDay(new Date());
      
      // Separate cruises into sailed (past and current) and booked (future)
      // A cruise is considered "sailed" if departure date is today or earlier (boarding day counts as sailed)
      const sailedCruises = cruises.filter(c => 
        c.cruiseLine === cruiseLineName && 
        startOfDay(parseISO(c.departureDate)) <= now
      );
      const bookedCruises = cruises.filter(c => 
        c.cruiseLine === cruiseLineName && 
        isAfter(startOfDay(parseISO(c.departureDate)), now)
      );

      // Get unique ships from sailed cruises (past cruises only) - use lowercase for case-insensitive matching
      const sailedShipsSet = new Set(sailedCruises.map(c => c.shipName.toLowerCase()));
      const bookedShipsSet = new Set(bookedCruises.map(c => c.shipName.toLowerCase()));

      // Separate active ships into sailed, under construction, booked, and not sailed
      const sailedShips = activeShips
        .filter(ship => sailedShipsSet.has(ship.toLowerCase()))
        .sort();
      
      // Identify under construction ships (including any booked ships under construction)
      const underConstructionShips = activeShips
        .filter(ship => isShipUnderConstruction(cruiseLineName, ship, sailedShipsSet))
        .sort();

      // Booked ships are those that are booked, not sailed, and not under construction
      const bookedShips = activeShips
        .filter(ship => {
          const shipLower = ship.toLowerCase();
          return bookedShipsSet.has(shipLower) && 
                 !sailedShipsSet.has(shipLower) && 
                 !underConstructionShips.includes(ship);
        })
        .sort();
      
      // Not sailed ships are those that are not sailed, not booked, and not under construction
      const notSailedShips = activeShips
        .filter(ship => {
          const shipLower = ship.toLowerCase();
          return !sailedShipsSet.has(shipLower) && 
                 !bookedShipsSet.has(shipLower) &&
                 !underConstructionShips.includes(ship);
        })
        .sort();

      // Get retired ships that have been sailed (case-insensitive matching)
      const retiredShipsSailed = retiredShips
        .filter(ship => sailedShipsSet.has(ship.toLowerCase()))
        .sort();

      const sailedCount = sailedShips.length;
      // Exclude under construction ships from total active ships count
      const totalActiveShips = activeShips.length - underConstructionShips.length;
      const percentage = totalActiveShips > 0 
        ? Math.round((sailedCount / totalActiveShips) * 100) 
        : 0;

      collections.push({
        cruiseLineName,
        totalActiveShips,
        sailedShips,
        notSailedShips,
        bookedShips,
        underConstructionShips,
        sailedCount,
        percentage,
        retiredShipsSailed,
        allBookedShips: Array.from(bookedShipsSet)
      });
    });

    // Sort by percentage (descending), then by name
    return collections.sort((a, b) => {
      if (b.percentage !== a.percentage) {
        return b.percentage - a.percentage;
      }
      return a.cruiseLineName.localeCompare(b.cruiseLineName);
    });
  }, [cruises, cruiseLines, getShipsForCruiseLine, getShipMetadata, itineraryCache]);

  const toggleExpanded = (cruiseLineName: string) => {
    setExpandedLines(prev => {
      const newSet = new Set(prev);
      if (newSet.has(cruiseLineName)) {
        newSet.delete(cruiseLineName);
      } else {
        newSet.add(cruiseLineName);
      }
      return newSet;
    });
  };

  const handleShipClick = useCallback((cruiseLineName: string, shipName: string) => {
    try {
      sessionStorage.setItem('collectionScrollY', window.scrollY.toString());
      sessionStorage.setItem('collectionTargetShip', `${cruiseLineName}___${shipName}`);
      // Ensure this cruise line is included in expanded lines
      setExpandedLines(prev => {
        const updated = new Set(prev);
        updated.add(cruiseLineName);
        try {
          sessionStorage.setItem('collectionExpandedLines', JSON.stringify(Array.from(updated)));
        } catch {
          // Ignore quota
        }
        return updated;
      });
    } catch (e) {
      console.error('Error saving collection navigation state:', e);
    }
  }, []);

  // Show skeleton until both data is loaded AND minimum time has elapsed
  if (!dataLoaded || !minTimeElapsed) {
    return <CollectionSkeleton />;
  }

  if (collectionData.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="space-y-8"
      >
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Ship Collection</h1>
          <p className="text-gray-600 mt-1">Track the ships you've sailed</p>
        </div>
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <Ship className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">No Ships Collected Yet</h3>
          <p className="text-gray-500">
            Start adding cruises to begin building your ship collection!
          </p>
        </div>
      </motion.div>
    );
  }

  // Calculate overall statistics (excluding under construction ships)
  const totalShipsSailed = collectionData.reduce((sum, col) => sum + col.sailedCount, 0);
  const totalActiveShips = collectionData.reduce((sum, col) => sum + col.totalActiveShips, 0);
  const overallPercentage = totalActiveShips > 0 
    ? Math.round((totalShipsSailed / totalActiveShips) * 100) 
    : 0;

  return (
    <motion.div
      className="space-y-8"
      initial={isRestoringScroll ? false : { opacity: 0 }}
      animate={{ opacity: dataLoaded ? 1 : 0 }}
      transition={{ duration: isRestoringScroll ? 0 : 0.3, ease: "easeOut" }}
    >
      <div>
        <h1 className="text-3xl font-bold text-gray-800">Ship Collection</h1>
        <p className="text-gray-600 mt-1">Track the ships you've sailed across all cruise lines</p>
      </div>

      {/* Overall Statistics */}
      <motion.div
        initial={isRestoringScroll ? false : { opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-br from-teal-900 to-teal-700 rounded-lg shadow-md p-6 text-white"
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold mb-1">Overall Collection</h2>
            <p className="text-teal-200 text-sm">
              {collectionData.length} cruise line{collectionData.length !== 1 ? 's' : ''}
            </p>
          </div>
          <Trophy className="h-12 w-12 text-teal-300" />
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-teal-100">Total Ships Sailed</span>
            <span className="text-2xl font-bold">
              {totalShipsSailed} / {totalActiveShips}
            </span>
          </div>
          <div className="w-full bg-teal-800 rounded-full h-3">
            <motion.div
              initial={isRestoringScroll ? false : { width: 0 }}
              animate={{ width: `${overallPercentage}%` }}
              transition={{ duration: isRestoringScroll ? 0 : 0.8, ease: "easeOut" }}
              className="bg-teal-300 h-3 rounded-full"
            />
          </div>
          <p className="text-teal-200 text-sm text-right">{overallPercentage}% complete</p>
          <p className="text-teal-300 text-xs text-right mt-2 opacity-75">
            Retired and Under Construction ships excluded from count; shown for reference only
          </p>
        </div>
      </motion.div>

      {/* Cruise Line Cards */}
      <div className="space-y-4">
        {collectionData.map((collection) => {
          const isExpanded = expandedLines.has(collection.cruiseLineName);
          const hasSailedShips = collection.sailedShips.length > 0;
          const hasNotSailedShips = collection.notSailedShips.length > 0;
          const hasBookedShips = collection.bookedShips.length > 0;
          const hasUnderConstructionShips = collection.underConstructionShips.length > 0;
          const hasRetiredShipsSailed = collection.retiredShipsSailed.length > 0;
          const hasNotSailedOrBooked = hasNotSailedShips || hasBookedShips;

          return (
            <motion.div
              key={collection.cruiseLineName}
              initial={isRestoringScroll ? false : { opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: isRestoringScroll ? 0 : 0.3 }}
              className="bg-white rounded-lg shadow-md overflow-hidden"
            >
              {/* Header - Always Visible */}
              <button
                onClick={() => toggleExpanded(collection.cruiseLineName)}
                className="w-full p-6 hover:bg-gray-50 transition-colors"
              >
                {/* Desktop Layout */}
                <div className="hidden md:flex items-center justify-between">
                  <div className="flex items-center space-x-4 flex-1">
                    <div className="flex-shrink-0">
                      <Anchor className="h-8 w-8 text-teal-600" />
                    </div>
                    <div className="flex-1 text-left">
                      <h3 className="text-xl font-semibold text-gray-800 mb-1">
                        {collection.cruiseLineName}
                      </h3>
                      <div className="flex items-center space-x-4">
                        <span className="text-2xl font-bold text-teal-600">
                          {collection.sailedCount} / {collection.totalActiveShips}
                        </span>
                        <span className="text-sm text-gray-500">ships sailed</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="w-32 bg-gray-200 rounded-full h-2">
                      <motion.div
                        initial={isRestoringScroll ? false : { width: 0 }}
                        animate={{ width: `${collection.percentage}%` }}
                        transition={{ duration: isRestoringScroll ? 0 : 0.8, ease: "easeOut" }}
                        className="bg-teal-600 h-2 rounded-full"
                      />
                    </div>
                    <span className="text-sm font-medium text-gray-600 w-12 text-right">
                      {collection.percentage}%
                    </span>
                    {isExpanded ? (
                      <ChevronUp className="h-5 w-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-400" />
                    )}
                  </div>
                </div>

                {/* Mobile Layout */}
                <div className="md:hidden space-y-3">
                  <div className="flex items-center space-x-3">
                    <Anchor className="h-6 w-6 text-teal-600 flex-shrink-0" />
                    <h3 className="text-lg font-bold text-gray-800 flex-1">
                      {collection.cruiseLineName}
                    </h3>
                    {isExpanded ? (
                      <ChevronUp className="h-5 w-5 text-gray-400 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-400 flex-shrink-0" />
                    )}
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-xl font-bold text-teal-600">
                      {collection.sailedCount} / {collection.totalActiveShips}
                    </span>
                    <span className="text-sm text-gray-500">ships sailed</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <motion.div
                        initial={isRestoringScroll ? false : { width: 0 }}
                        animate={{ width: `${collection.percentage}%` }}
                        transition={{ duration: isRestoringScroll ? 0 : 0.8, ease: "easeOut" }}
                        className="bg-teal-600 h-2 rounded-full"
                      />
                    </div>
                    <span className="text-sm font-medium text-gray-600 w-12 text-right flex-shrink-0">
                      {collection.percentage}%
                    </span>
                  </div>
                </div>
              </button>

              {/* Expanded Content */}
              <AnimatePresence initial={false}>
                {isExpanded && (
                  <motion.div
                    initial={false}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="px-6 pb-6 border-t border-gray-200">
                      {/* Sailed Ships */}
                      {hasSailedShips && (
                        <div className="mt-6">
                          <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                            <CheckCircle className="h-5 w-5 text-teal-600 mr-2" />
                            Ships Sailed ({collection.sailedShips.length})
                          </h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4">
                            {collection.sailedShips.map(shipName => (
                              <ShipCard
                                key={shipName}
                                shipName={shipName}
                                sailed={true}
                                cruiseLineName={collection.cruiseLineName}
                                shipImageUrl={getShipImageUrl(collection.cruiseLineName, shipName)}
                                onShipClick={handleShipClick}
                              />
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Not Sailed Ships (includes booked ships) */}
                      {hasNotSailedOrBooked && (
                        <div className={hasSailedShips ? 'mt-8' : 'mt-6'}>
                          <h4 className="text-lg font-semibold text-gray-500 mb-4 flex items-center">
                            <XCircle className="h-5 w-5 text-gray-500 mr-2" />
                            Ships Not Sailed ({collection.notSailedShips.length + collection.bookedShips.length})
                          </h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4">
                            {/* Show booked ships first */}
                            {collection.bookedShips.map(shipName => (
                              <ShipCard
                                key={shipName}
                                shipName={shipName}
                                sailed={false}
                                isBooked={true}
                                cruiseLineName={collection.cruiseLineName}
                                shipImageUrl={getShipImageUrl(collection.cruiseLineName, shipName)}
                                onShipClick={handleShipClick}
                              />
                            ))}
                            {/* Then show not sailed ships */}
                            {collection.notSailedShips.map(shipName => (
                              <ShipCard
                                key={shipName}
                                shipName={shipName}
                                sailed={false}
                                cruiseLineName={collection.cruiseLineName}
                                shipImageUrl={getShipImageUrl(collection.cruiseLineName, shipName)}
                                onShipClick={handleShipClick}
                              />
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Under Construction Ships */}
                      {hasUnderConstructionShips && (
                        <div className={(hasSailedShips || hasNotSailedOrBooked) ? 'mt-8' : 'mt-6'}>
                          <h4 className="text-lg font-semibold text-gray-500 mb-4 flex items-center">
                            <Wrench className="h-5 w-5 text-gray-500 mr-2" />
                            Under Construction ({collection.underConstructionShips.length})
                          </h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4">
                            {collection.underConstructionShips.map(shipName => (
                              <ShipCard
                                key={shipName}
                                shipName={shipName}
                                sailed={false}
                                isBooked={collection.allBookedShips.includes(shipName.toLowerCase())}
                                underConstruction={true}
                                cruiseLineName={collection.cruiseLineName}
                                shipImageUrl={getShipImageUrl(collection.cruiseLineName, shipName)}
                                onShipClick={handleShipClick}
                              />
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Retired Ships Sailed */}
                      {hasRetiredShipsSailed && (
                        <div className={(hasSailedShips || hasNotSailedOrBooked || hasUnderConstructionShips) ? 'mt-8' : 'mt-6'}>
                          <h4 className="text-lg font-semibold text-gray-600 mb-4 flex items-center">
                            <Anchor className="h-5 w-5 text-gray-500 mr-2" />
                            Retired Ships Sailed ({collection.retiredShipsSailed.length})
                          </h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4">
                            {collection.retiredShipsSailed.map(shipName => (
                              <ShipCard
                                key={shipName}
                                shipName={shipName}
                                sailed={true}
                                cruiseLineName={collection.cruiseLineName}
                                shipImageUrl={getShipImageUrl(collection.cruiseLineName, shipName)}
                                onShipClick={handleShipClick}
                              />
                            ))}
                          </div>
                        </div>
                      )}

                      {!hasSailedShips && !hasNotSailedOrBooked && !hasUnderConstructionShips && !hasRetiredShipsSailed && (
                        <div className="mt-6 text-center py-8 text-gray-500">
                          <p>No ship data available for this cruise line.</p>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}
      </div>
    </motion.div>
  );
};

export default Collection;

